// pages/AssetsPage/AssetsPage.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Componentes
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import ItemList from '../../components/ItemList/ItemList';
import FriendlyPhrase from '../../components/FriendlyPhrase/FriendlyPhrase';

// Contextos
import { useToast } from '../../contexts/ToastContext';
import { useLoader } from '../../contexts/LoaderContext';
import { useModal } from '../../contexts/ModalContext';

// API
import { useTesteService } from '../../services/testeServices';
import { useAlgoritmoTesteService } from '../../services/algoritmoTesteServices';
import { useReportAssetService } from '../../services/reportAssetsService';

// Estilos
import styles from './assetspage.module.css';

function AssetsPage() {
  const navigate = useNavigate();

  const { showToast } = useToast();
  const { showLoader, hideLoader } = useLoader();
  const { showModal } = useModal();

  const { listarTestes } = useTesteService();
  const { listarAlgoritmosPorTeste } = useAlgoritmoTesteService();
  const { listarTodosDoAlgoritmo, deletarAsset } = useReportAssetService();

  // Agora esta lista representa **algoritmos com arquivos**
  const [testesItems, setTestesItems] = useState([]);

  // Estado do seletor (onAdd)
  const [seletorAberto, setSeletorAberto] = useState(false);
  const [testesSelect, setTestesSelect] = useState([]);
  const [testeSelecionado, setTesteSelecionado] = useState('');

  // --- Helpers ---
  const normalizeTestes = (resp) => {
    const testes =
      (resp?.data && Array.isArray(resp.data) && resp.data) ||
      (resp?.testes && Array.isArray(resp.testes) && resp.testes) ||
      (Array.isArray(resp) ? resp : []) ||
      [];
    return Array.isArray(testes) ? testes : [];
  };

  const getTesteId = (t) =>
    t.idEncoded ?? t.testeIdEncoded ?? t.id ?? t.teste_id ?? t.testeId;

  const getTesteNome = (t) => t.nome ?? t.titulo ?? t.name ?? '(sem nome)';

  const getAlgId = (a) =>
    a.idEncoded ?? a.algoritmoIdEncoded ?? a.id ?? a.algoritmo_id ?? a.algoritmoId;

  const getAlgNome = (a) => a.titulo ?? a.nome ?? a.name ?? '(algoritmo)';

  const getAssetId = (a) =>
    a?.idEncoded ?? a?.assetId ?? a?.asset_id ?? a?.id;

  const filtrarTestesComAlgoritmoPublicado = async (testes) => {
    const checks = await Promise.allSettled(
      testes.map(async (t) => {
        const idTeste = getTesteId(t);
        if (!idTeste) return { hasPublished: false, idTeste: null, nome: null };

        // status=published e limit=1 para checagem rápida
        const res = await listarAlgoritmosPorTeste({
          testeId: idTeste,
          status: 'published',
          limit: 1,
        });

        const list = (res?.data && Array.isArray(res.data) && res.data) || [];
        return {
          hasPublished: list.length > 0,
          idTeste,
          nome: getTesteNome(t),
        };
      })
    );

    return checks
      .filter((r) => r.status === 'fulfilled' && r.value.hasPublished)
      .map((r) => r.value);
  };

  // Carrega **algoritmos com arquivos** (1 item por algoritmo, mesmo que tenha N arquivos)
  useEffect(() => {
    const carregarAlgoritmosComArquivos = async () => {
      showLoader('Carregando algoritmos com arquivos de devolutivas');
      try {
        const respTestes = await listarTestes();
        const testes = normalizeTestes(respTestes);

        if (!Array.isArray(testes) || testes.length === 0) {
          showToast('Nenhum teste encontrado', 3000, 'warning');
          setTestesItems([]);
          return;
        }

        // 1) Mantemos a regra: considerar apenas testes que possuam algoritmos published
        const publicados = await filtrarTestesComAlgoritmoPublicado(testes);

        if (publicados.length === 0) {
          showToast('Não há testes com algoritmos publicados', 3500, 'info');
          setTestesItems([]);
          return;
        }

        // 2) Para cada teste publicado, buscamos TODOS os algoritmos publicados
        // 3) Para cada algoritmo, checamos se há arquivos vinculados (onlyActive=true)
        const perTesteSettled = await Promise.allSettled(
          publicados.map(async ({ idTeste, nome: testeNome }) => {
            const resAlgs = await listarAlgoritmosPorTeste({
              testeId: idTeste,
              status: 'published',
              // sem limit aqui para varrer todos; se for muito grande, adicione paginação no service
            });

            const algs =
              (resAlgs?.data && Array.isArray(resAlgs.data) && resAlgs.data) || [];

            // Para cada algoritmo, checa ativos
            const perAlgSettled = await Promise.allSettled(
              algs.map(async (a) => {
                const algoritmoId = getAlgId(a);
                if (!algoritmoId) return null;

                const resAssets = await listarTodosDoAlgoritmo(algoritmoId, { onlyActive: true });
                const assets =
                  (resAssets?.data && Array.isArray(resAssets.data) && resAssets.data) || [];

                if (assets.length === 0) return null;

                // Há pelo menos 1 arquivo ativo: incluímos este algoritmo **uma única vez**
                return {
                  id: String(algoritmoId),     // id do item = algoritmoId (string)
                  name: `${testeNome} — ${getAlgNome(a)}`, // rótulo visível
                  _testeId: String(idTeste),
                  _testeNome: testeNome,
                  _algId: String(algoritmoId),
                  _algNome: getAlgNome(a),
                  _assetCount: assets.length,  // opcional (pode ser útil em badges futuramente)
                };
              })
            );

            // Filtra nulos/erros e retorna a lista de itens deste teste
            return perAlgSettled
              .filter((r) => r.status === 'fulfilled' && r.value)
              .map((r) => r.value);
          })
        );

        // Achata tudo em uma única lista
        const itensAlgoritmos = perTesteSettled
          .filter((r) => r.status === 'fulfilled')
          .flatMap((r) => r.value || []);

        if (itensAlgoritmos.length === 0) {
          showToast('Não há algoritmos com arquivos vinculados', 3500, 'info');
        }

        // Caso algum service retorne duplicados por algum motivo, dedup por _algId
        const seen = new Set();
        const dedup = [];
        for (const it of itensAlgoritmos) {
          if (!seen.has(it._algId)) {
            seen.add(it._algId);
            dedup.push(it);
          }
        }

        setTestesItems(dedup);
      } catch (e) {
        console.error('[AssetsPage] erro ao carregar algoritmos/arquivos:', e);
        showToast('Erro ao carregar dados', 3000, 'error');
        setTestesItems([]);
      } finally {
        hideLoader();
      }
    };

    carregarAlgoritmosComArquivos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToUpdateForAlgorithm = (item) => {
    navigate(`/devolutivas/${item._testeId}/atualizar?alg=${item._algId}`);
  };

  const handleClickItem = (item) => {
    goToUpdateForAlgorithm(item);
  };

  const handleEdit = (item) => {
    goToUpdateForAlgorithm(item);
  };

  const handleDelete = (item) => {
    const algoritmoId = item?._algId ?? item?.id ?? item?.algoritmo_id ?? item?.algoritmoId;
    const algoritmoNome = item?.name ?? item?._algNome ?? 'algoritmo';

    console.group('[AssetsPage] handleDelete');
    console.log('Item recebido:', item);
    console.log('Algoritmo resolvido:', { algoritmoId, algoritmoNome });

    if (!algoritmoId) {
      console.warn('[AssetsPage] handleDelete: algoritmoId ausente/inválido');
      showToast('Não foi possível identificar o algoritmo selecionado.', 3500, 'warning');
      console.groupEnd();
      return;
    }

    showModal(
      `Excluir TODOS os arquivos de ${algoritmoNome}?`,
      'Essa ação não poderá ser desfeita. Todos os arquivos vinculados a este algoritmo serão removidos.',
      async () => {
        console.time('[AssetsPage] Exclusão total');
        try {
          showLoader('Coletando arquivos do algoritmo...');
          console.log('[AssetsPage] Buscando assets do algoritmo...', { algoritmoId });

          // Buscar TODOS os assets (não apenas ativos)
          const resAssets = await listarTodosDoAlgoritmo(algoritmoId, { onlyActive: false });
          console.log('[AssetsPage] Resposta listarTodosDoAlgoritmo:', resAssets);

          const assets =
            (resAssets?.data && Array.isArray(resAssets.data) && resAssets.data) || [];

          console.log(`[AssetsPage] Total de assets encontrados para o algoritmo ${algoritmoId}:`, assets.length);
          if (!assets.length) {
            showToast('Este algoritmo não possui arquivos para excluir.', 3000, 'info');
            console.groupEnd();
            return;
          }

          // Extrai IDs e loga a tabela
          const getAssetId = (a) => a?.idEncoded ?? a?.assetId ?? a?.asset_id ?? a?.id;
          const assetIds = assets.map(getAssetId).filter(Boolean);

          console.table(
            assetIds.map((id, idx) => ({ '#': idx + 1, assetId: String(id) })),
          );

          // Excluir todos os assets
          const total = assetIds.length;
          showLoader(`Excluindo ${total} arquivo(s)...`);
          console.time('[AssetsPage] Deleções (Promise.allSettled)');

          const results = await Promise.allSettled(
            assetIds.map(async (id, idx) => {
              console.time(`[AssetsPage] deletarAsset #${idx + 1} (${id})`);
              try {
                const resp = await deletarAsset(String(id));
                console.log(`[AssetsPage] deletarAsset OK #${idx + 1} (${id}) ->`, resp);
                return resp;
              } catch (err) {
                console.error(`[AssetsPage] deletarAsset ERRO #${idx + 1} (${id}) ->`, err);
                throw err;
              } finally {
                console.timeEnd(`[AssetsPage] deletarAsset #${idx + 1} (${id})`);
              }
            })
          );

          console.timeEnd('[AssetsPage] Deleções (Promise.allSettled)');

          // Avaliar resultados
          let success = 0;
          let failed = 0;
          const detalhes = [];

          for (let i = 0; i < results.length; i++) {
            const r = results[i];
            const id = assetIds[i];

            if (r.status === 'fulfilled') {
              const resp = r.value;
              const ok = resp?.success ?? resp?.ok ?? (resp?.status === 'success');
              detalhes.push({ assetId: String(id), status: ok ? 'success' : 'fail', resp });
              if (ok) success++;
              else failed++;
            } else {
              detalhes.push({ assetId: String(id), status: 'rejected', reason: r.reason });
              failed++;
            }
          }

          console.log('[AssetsPage] Resumo exclusões:', { success, failed, total });
          console.table(detalhes.map(d => ({ assetId: d.assetId, status: d.status })));

          if (success > 0 && failed === 0) {
            showToast(`Todos os ${success} arquivo(s) foram excluídos!`, 3200, 'success');
          } else if (success > 0 && failed > 0) {
            showToast(
              `Excluídos ${success} arquivo(s), ${failed} falhou(aram).`,
              4000,
              'warning'
            );
          } else {
            showToast('Falha ao excluir os arquivos.', 3500, 'error');
          }

          // Se todos foram processados (sucesso + falha = total), removemos o algoritmo da lista.
          // (Você pode optar por remover apenas se failed === 0, mas aqui removemos porque o objetivo é zerar)
          if (success + failed === total) {
            console.log('[AssetsPage] Removendo algoritmo da lista local:', { algoritmoId });
            setTestesItems((prev) =>
              Array.isArray(prev)
                ? prev.filter((i) => String(i._algId) !== String(algoritmoId))
                : prev
            );
          }
        } catch (e) {
          console.error('[AssetsPage] erro ao excluir assets do algoritmo:', e);
          showToast('Erro ao excluir os arquivos do algoritmo.', 3500, 'error');
        } finally {
          hideLoader();
          console.timeEnd('[AssetsPage] Exclusão total');
          console.groupEnd();
        }
      },
      () => {
        console.log('[AssetsPage] handleDelete: exclusão cancelada pelo usuário.');
        showToast('Exclusão cancelada.', 2000);
        console.groupEnd();
      }
    );
  };

  const handleAdd = async () => {
    showLoader('Carregando testes');
    try {
      const resp = await listarTestes();
      const testes = normalizeTestes(resp);

      if (!Array.isArray(testes) || testes.length === 0) {
        showToast('Nenhum teste encontrado', 3000, 'warning');
        setTestesSelect([]);
        setTesteSelecionado('');
        setSeletorAberto(true);
        return;
      }

      const publicados = await filtrarTestesComAlgoritmoPublicado(testes);

      if (publicados.length === 0) {
        showToast('Não há testes com algoritmos publicados para selecionar', 3500, 'info');
      }

      const options = publicados.map(({ idTeste, nome }) => ({
        id: idTeste,
        nome,
      }));

      setTestesSelect(options);
      setTesteSelecionado(options[0]?.id || '');
      setSeletorAberto(true);
    } catch (e) {
      console.error('[AssetsPage] erro no handleAdd:', e);
      showToast('Erro ao carregar testes', 3000, 'error');
      setTestesSelect([]);
      setTesteSelecionado('');
      setSeletorAberto(true);
    } finally {
      hideLoader();
    }
  };

  const cancelarSeletor = () => setSeletorAberto(false);

  const confirmarSeletor = () => {
    if (!testeSelecionado) {
      showToast('Selecione um teste.', 2500, 'info');
      return;
    }
    setSeletorAberto(false);
    // Continua levando para a tela de adicionar por teste (fluxo existente)
    navigate(`/devolutivas/${testeSelecionado}/adicionarDevolutivas`);
  };

  return (
    <div className={styles.AssetsPage}>
      <div className={styles.containerTop}>
        <Breadcrumbs />
        <h1 className={styles.title}>Arquivos de Devolutivas</h1>
        <FriendlyPhrase page="report-assets" />
      </div>

      <div className={styles.containerList}>
        <ItemList
          // Agora `items` são algoritmos com arquivos (1 item por algoritmo)
          items={testesItems}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onClickItem={handleClickItem}
          labelField="name"
        />
      </div>

      {seletorAberto && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h2 className={styles.modalTitle}>Selecionar teste</h2>

            {testesSelect.length > 0 ? (
              <>
                <div className={styles.modalInfo}>
                  <p>Escolha o teste para criar novas devolutivas:</p>
                  <select
                    className={styles.selectControl}
                    value={testeSelecionado}
                    onChange={(e) => setTesteSelecionado(e.target.value)}
                  >
                    <option value="" disabled>Selecionar</option>
                    {testesSelect.map((t) => (
                      <option key={t.id} value={t.id}>{t.nome}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.modalActions}>
                  <button className={styles.secondaryButton} onClick={cancelarSeletor}>
                    Cancelar
                  </button>
                  <button className={styles.primaryButton} onClick={confirmarSeletor}>
                    Continuar
                  </button>
                </div>
              </>
            ) : (
              <>
                <p>Não há testes com algoritmos publicados para seleção.</p>
                <div className={styles.modalActions}>
                  <button className={styles.primaryButton} onClick={cancelarSeletor}>
                    Fechar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AssetsPage;